import Constants from '@/constant'
// states
const _state = {

}

// The three readings both getters below are built out of, so the pair can never disagree about
// what they are looking at.
const isPresentingWithPPT = (rootState, rootGetters) => Boolean(
  rootState.isEnableAddinPowerpoint && rootGetters.isPresenting,
)

const hasEditPermissions = (rootState) => Boolean(
  rootState.presentation.permissions &&
  rootState.presentation.permissions.includes(Constants.permissions.RENAME_PRESENTATION),
)

// Whether the presentation on screen is the copy that was cloned FOR this presenter, rather
// than the deck it was cloned from. A presenter-role collaborator is redirected onto their own
// copy before the editor mounts (PresentationRedirectForPresenter), and that copy is owned by the
// master-presentation system user — which is what isPresenterBeingSharedByEditor recognises, and
// also why they hold no edit rights on a presentation that is, in every way that matters, theirs.
//
// Read defensively through rootGetters: this module is mounted in stores that do not carry the
// multipleSessions module, and a presenter copy is the exception rather than the default, so its
// absence has to read as "not one".
const isOwnPresenterCopy = (rootState, rootGetters) => {
  const isPresenterCopy = rootGetters['multipleSessions/isPresenterBeingSharedByEditor']
  return Boolean(isPresenterCopy && isPresenterCopy(rootState.presentation))
}

const isTakenOverByAnotherUser = (rootState) => (
  rootState.presentation.takenOverBy !== rootState.currentUser.id
)

// getters
const _getters = {
  isTakeOverToolbarVisible: (_, __, rootState, rootGetters) => (
    !isPresentingWithPPT(rootState, rootGetters) &&
    (!hasEditPermissions(rootState) || isTakenOverByAnotherUser(rootState))
  ),

  // AHA-47754: whether the toolbar above is showing a Take over BUTTON — which is not the same
  // question as whether the toolbar is there at all.
  //
  // It goes up for two different users. One has edit rights and is locked out only because
  // somebody else holds the presentation: TakeOverToolbar offers them "To edit please take over
  // from X" and the button that does it. The other — a collaborator with the Presenter role —
  // has no edit rights at all, and gets the view-only info bar, which has no button in it and
  // no state they can reach where it would appear.
  //
  // So anything gated on the toolbar's mere visibility tells that second user to take over
  // first, using a control that does not exist for them. This is the narrower condition to
  // gate on: a strict subset of isTakeOverToolbarVisible, made of the same readings, matching
  // the branch that actually renders the button.
  isTakeOverAvailable: (_, __, rootState, rootGetters) => (
    !isPresentingWithPPT(rootState, rootGetters) &&
    hasEditPermissions(rootState) &&
    isTakenOverByAnotherUser(rootState)
  ),

  // AHA-47754: the other half of that split — a user with no edit rights who is looking at a
  // presentation that is not their own copy of one.
  //
  // The distinction matters to anything that would START PRESENTING the deck on screen, which is
  // not the read-only act it looks like: the API gates those writes on take-over-to-edit, a
  // permission the viewer and instructor roles both carry, so the server accepts a presenting
  // write from a user this app calls view-only and the client is the only thing deciding whose
  // session it lands in. On the presenter's own copy it lands in theirs and nobody else sees it.
  // On the deck it was cloned from, it takes the show off whoever is running it.
  //
  // Like isTakeOverAvailable this is a strict subset of isTakeOverToolbarVisible — no edit rights
  // is one of the two things that put the bar up — so neither condition can grey a control out in
  // a state where nothing on screen is explaining why.
  isViewOnlyOnAnotherUsersPresentation: (_, __, rootState, rootGetters) => (
    !isPresentingWithPPT(rootState, rootGetters) &&
    !hasEditPermissions(rootState) &&
    !isOwnPresenterCopy(rootState, rootGetters)
  ),
}

// mutations
const mutations = {

}

// actions
const actions = {

}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
